import type { UseFormReturn } from "react-hook-form";
import {
  Button,
  Checkbox,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  TextInput,
  Typography,
} from "@/components/ui";
import { componentTypeOptions } from "@/pages/public/repairIntakeForm/repairIntakeFormConstants";
import type { RepairIntakeFormData } from "@/store/useRepairIntakeStore";

type RepairIntakeSection1Props = {
  form: UseFormReturn<RepairIntakeFormData>;
  componentTypes: string[];
  componentEntryCount: number;
  componentEntryIds: string[];
  onToggleComponentType: (value: string) => void;
  onAddComponent: () => void;
  onRemoveComponent: (index: number) => void;
};

export function RepairIntakeSection1({
  form,
  componentTypes,
  componentEntryCount,
  componentEntryIds,
  onToggleComponentType,
  onAddComponent,
  onRemoveComponent,
}: RepairIntakeSection1Props) {
  return (
    <>
      <div className="space-y-3">
        <Typography variant="text-small" weight="medium">
          1. What is the Component Type *
        </Typography>
        <div className="grid gap-3 sm:grid-cols-2">
          {componentTypeOptions.map((option) => (
            <Checkbox
              key={option}
              checked={componentTypes.includes(option)}
              label={option}
              onChange={() => onToggleComponentType(option)}
            />
          ))}
          <Checkbox
            checked={componentTypes.includes("Other")}
            label="Other:"
            onChange={() => {
              onToggleComponentType("Other");
              if (componentTypes.includes("Other")) {
                form.setValue("section1.componentTypeOther", "", {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                });
              }
            }}
          />
        </div>
        {componentTypes.includes("Other") ? (
          <FormField
            control={form.control}
            name="section1.componentTypeOther"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TextInput placeholder="Other component type" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <Typography variant="validationError">{form.formState.errors.section1?.componentTypes?.message}</Typography>
      </div>

      <FormField
        control={form.control}
        name="section1.materialsGrades"
        render={({ field }) => (
          <FormItem>
            <FormLabel>2. What is the Materials & Grades *</FormLabel>
            <FormControl>
              <TextInput placeholder="SA106 Gr B, 316 Stainless Steel, Carbon Steel" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <Typography variant="text-small" weight="medium">
            Component Sizes
          </Typography>
          <Button type="button" variant="outline" onClick={onAddComponent}>
            +
          </Button>
        </div>

        {componentEntryIds.map((fieldId, index) => (
          <div key={fieldId} className="space-y-4 rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between gap-4">
              <Typography weight="semibold">Component {index + 1}</Typography>
              <Button
                type="button"
                variant="outline"
                onClick={() => onRemoveComponent(index)}
                disabled={componentEntryCount === 1}
              >
                Remove
              </Button>
            </div>

            <FormField
              control={form.control}
              name={`section1.componentEntries.${index}.nominalPipeSize`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>3. What is the Nominal Pipe Size *</FormLabel>
                  <FormControl>
                    <TextInput placeholder={'4", 8", 12"'} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`section1.componentEntries.${index}.outsideDiameter`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>4. What is the Outside Diameter *</FormLabel>
                  <FormControl>
                    <TextInput placeholder={'4.5", 8.625", 12.75"'} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`section1.componentEntries.${index}.schedule`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>5. What is the Schedule *</FormLabel>
                  <FormControl>
                    <TextInput placeholder="Schedule 40, Schedule 80" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`section1.componentEntries.${index}.nominalWallThickness`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>6. What is the Nominal Wall Thickness *</FormLabel>
                  <FormControl>
                    <TextInput {...field} />
                  </FormControl>
                  <FormDescription>Can be found in blue book with schedule</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ))}
      </div>
    </>
  );
}
